import { Container, Graphics, NineSliceSprite, type Texture } from 'pixi.js';
import { animate, COLOURS, ease } from '../board/ui';

/**
 * The painted gauge's channel inside its brass rim, in the track art's
 * pixels (board/gauge_track.png, 1024 x 116): the walnut groove runs from
 * 41 to 981 across and from 15 to 98 down, with round ends.
 */
const TRACK = { width: 1024, height: 116, left: 41, right: 43, top: 15, bottom: 18 };
/** The ends of the track (rivet and round groove end) keep their shape. */
const TRACK_SLICE = { leftWidth: 86, rightWidth: 86, topHeight: 0, bottomHeight: 0 };
/** The blue crystal fill (board/gauge_fill.png, 1024 x 122): round ends. */
const FILL_SLICE = { leftWidth: 64, rightWidth: 64, topHeight: 0, bottomHeight: 0 };
const FILL_HEIGHT = 122;

/**
 * A painted gauge: a brass-rimmed walnut groove that fills with blue gem
 * crystal from the left. Used for the gems saved towards a slot and for a
 * slot's download. Until its art loads it draws a plain stand-in.
 */
export class GemGauge extends Container {
  private readonly drawn = new Graphics();
  private track: NineSliceSprite | null = null;
  private fill: NineSliceSprite | null = null;
  private w = 200;
  private h = 24;
  private value = 0;
  private run = 0;

  constructor(art: { track: Texture | null; fill: Texture | null }) {
    super();
    this.addChild(this.drawn);
    this.setArt(art);
  }

  setArt(art: { track: Texture | null; fill: Texture | null }): void {
    if (art.track && this.track?.texture !== art.track) {
      this.track?.destroy();
      this.track = new NineSliceSprite({ texture: art.track, ...TRACK_SLICE });
      this.addChildAt(this.track, 0);
    }
    if (art.fill && this.fill?.texture !== art.fill) {
      this.fill?.destroy();
      this.fill = new NineSliceSprite({ texture: art.fill, ...FILL_SLICE });
      this.addChild(this.fill);
    }
    this.redraw();
  }

  /** How full it shows, from 0 to 1. */
  get fraction(): number {
    return this.value;
  }

  resize(width: number, height: number): void {
    this.w = Math.max(40, Math.round(width));
    this.h = Math.max(10, Math.round(height));
    this.redraw();
  }

  /** Show `fraction` (0 to 1) at once. */
  setValue(fraction: number): void {
    this.run += 1;
    this.value = clamp(fraction);
    this.redraw();
  }

  /** Fill from `from` to `fraction` over `ms` (the board opening). */
  async fillTo(fraction: number, ms = 650, from = 0): Promise<void> {
    const run = ++this.run;
    const to = clamp(fraction);
    const start = clamp(from);
    await animate(ms, (t) => {
      if (run !== this.run) return;
      this.value = start + (to - start) * ease.outCubic(t);
      this.redraw();
    });
  }

  private redraw(): void {
    const { w, h } = this;
    // The channel the fill runs in, in this gauge's own pixels.
    const k = h / TRACK.height;
    const inner = { x: TRACK.left * k, y: TRACK.top * k, w: w - (TRACK.left + TRACK.right) * k, h: h - (TRACK.top + TRACK.bottom) * k };
    const pad = Math.max(1, inner.h * 0.08);
    const fillH = inner.h - pad * 2;
    const fillX = inner.x + pad;
    const room = inner.w - pad * 2;
    this.drawn.clear();
    if (this.track) {
      this.track.scale.set(k);
      this.track.width = Math.max(TRACK_SLICE.leftWidth + TRACK_SLICE.rightWidth, w / k);
      this.track.height = TRACK.height;
    } else {
      this.drawn
        .roundRect(0, 0, w, h, h / 2)
        .fill({ color: COLOURS.brass })
        .roundRect(inner.x, inner.y, inner.w, inner.h, inner.h / 2)
        .fill({ color: COLOURS.woodDark });
    }
    const fillW = this.value <= 0 ? 0 : Math.max(fillH, room * this.value);
    if (this.fill) {
      const kf = fillH / FILL_HEIGHT;
      this.fill.visible = fillW > 0;
      this.fill.scale.set(kf);
      this.fill.width = Math.max(FILL_SLICE.leftWidth + FILL_SLICE.rightWidth, fillW / kf);
      this.fill.height = FILL_HEIGHT;
      this.fill.position.set(fillX, inner.y + pad);
    } else if (fillW > 0) {
      this.drawn.roundRect(fillX, inner.y + pad, fillW, fillH, fillH / 2).fill({ color: 0x3fb4f0 });
    }
  }
}

function clamp(n: number): number {
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
}
